import { useState, useEffect } from "react";
import {
  BookOpen,
  Clock,
  Download,
  Calendar,
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  Eye,
  User,
  RefreshCw,
  Trophy,
  Award,
  TrendingUp,
  Target,
  Video,
  Volume2,
  Code,
  ClipboardCheck,
  ExternalLink,
  File,
  BarChart3,
  ClipboardList,
  Filter,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { ApiService } from "../../../../services/api";
import ConfirmModal from "../../../../components/modals/ConfirmModal";
import checkPermission, { checkSuperAdmin } from "../../../../utils/permission";
import StudentReportView from "./StudentReportView";
import Modal from "../../../../components/ui/Modal";
import DynamicList from "../../../../components/dynamic/List";
import { useMemo } from "react";
import ReportFilter, { ReportStatusFilter, ReportContentTypeFilter, Department } from "./ReportFilter";
import TestHorizontalFilter, { TestReportStatusFilter } from "./TestHorizontalFilter";
import StudentReportsTab from "./StudentReportsTab";
import DepartmentHomeworkReportTab from "./DepartmentHomeworkReportTab";
import ParentReportsTab from "./ParentReportsTab";
import TeacherHomeworkReportTab from "./TeacherHomeworkReportTab";
import ErrorBookletReportsTab from "./ErrorBookletReportsTab";
import SuccessScoreReportTab from "./SuccessScoreReportTab";
import BranchSuccessScoreReportTab from "./BranchSuccessScoreReportTab";
import AIObservationTab from "./AIObservationTab";
import { StudentReportData, LessonOption, TopicOption, StudentReportType } from "../../../../types/student-report";

interface HomeworkReport {
  id: string;
  reportData: any;
  reportHtml: string;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
  homework: {
    id: string;
    title: string;
    status: string;
    deadline: string;
    createdAt: string;
    finishedAt?: string;
    metadata?: {
      time?: number;
      year?: number;
      [key: string]: any;
    };
    lesson: {
      id: string;
      title: string;
    };
    lessonSection: {
      id: string;
      title: string;
    };
    lessonSectionContent: {
      id: string;
      title: string;
      type: string;
    };
  };
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    metadata?: {
      schoolNumber?: string;
      tcNumber?: string;
      [key: string]: any;
    };
    department?: {
      id: string;
      name: string;
      color: string;
    };
  };
}

interface TestReport {
  id: string;
  status: "COMPLETED" | "FAILED" | "PENDING";
  score?: number;
  totalQuestions: number;
  correctAnswers: number;
  wrongAnswers: number;
  blankAnswers: number;
  startedAt?: string;
  completedAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    metadata?: {
      schoolNumber?: string;
      tcNumber?: string;
      [key: string]: any;
    };
    department?: {
      id: string;
      name: string;
      color: string;
    };
  };
  test: {
    id: string;
    title: string;
    class: string;
    subClass: string;
    lesson: string;
    topic: string;
  };
}

interface ReportSummary { }

export default function ReportsView() {
  const hasHomeworkReportList = checkPermission("homework_report_list");
  const hasTestReportList = checkPermission("test_report_list");
  const hasStudentReportList = checkPermission("student_report_list");
  const hasDepartmentReportList = checkPermission("department_homework_report_list");
  const hasParentReportList = checkPermission("parent_report_list");
  const hasTeacherReportList = checkPermission("homework_report_list");
  const hasErrorBookletReportList = checkPermission("homework_report_list");
  const hasSuccessScoreReportList = checkPermission("department_homework_report_list");
  const hasAIObservation = checkSuperAdmin() || checkPermission("navigator_observe");

  const isParentOnly = hasParentReportList &&
    !hasHomeworkReportList &&
    !hasTestReportList &&
    !hasDepartmentReportList;

  const getInitialTab = (): 'homework' | 'test' | 'student' | 'department' | 'parent' | 'teacher' | 'error-booklet' | 'success-score' | 'monthly-success-score' | 'branch-success-score' => {
    if (hasParentReportList) return "parent";
    if (hasHomeworkReportList) return "homework";
    if (hasTestReportList) return "test";
    if (hasStudentReportList) return "student";
    if (hasDepartmentReportList) return "department";
    return "homework";
  };

  const [activeTab, setActiveTab] = useState<'homework' | 'test' | 'student' | 'department' | 'parent' | 'teacher' | 'error-booklet' | 'success-score' | 'monthly-success-score' | 'branch-success-score' | 'ai-observation'>(getInitialTab());
  const [summary] = useState<ReportSummary | null>(null);
  const [summaryLoading] = useState(false);
  const [viewingReport, setViewingReport] = useState<HomeworkReport | null>(null);
  const [viewingTestReport, setViewingTestReport] = useState<any | null>(null);
  const [loadingTestReport, setLoadingTestReport] = useState(false);

  const [filterOpen, setFilterOpen] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<ReportStatusFilter>("all");
  const [selectedContentType, setSelectedContentType] = useState<ReportContentTypeFilter>("all");
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [rows, setRows] = useState<HomeworkReport[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(false);

  const [testSelectedClass, setTestSelectedClass] = useState<string | null>(null);
  const [testSelectedSubClass, setTestSelectedSubClass] = useState<string[]>([]);
  const [testParentDepartmentId, setTestParentDepartmentId] = useState<string | null>(null);
  const [testSelectedLesson, setTestSelectedLesson] = useState<string | null>(null);
  const [testSelectedTopic, setTestSelectedTopic] = useState<string | null>(null);
  const [testSelectedTest, setTestSelectedTest] = useState<string[]>([]);
  const [testSelectedStatus, setTestSelectedStatus] = useState<TestReportStatusFilter>("all");
  const [testStartDate, setTestStartDate] = useState<string | null>(null);
  const [testEndDate, setTestEndDate] = useState<string | null>(null);
  const [testReports, setTestReports] = useState<TestReport[]>([]);
  const [testReportsLoading, setTestReportsLoading] = useState(false);
  const [testFiltered, setTestFiltered] = useState(false);
  const [testParentDepartmentName, setTestParentDepartmentName] = useState<string | null>(null);
  const [testSubClassName, setTestSubClassName] = useState<string | null>(null);
  const [testLessonTitle, setTestLessonTitle] = useState<string | null>(null);
  const [testTopicTitle, setTestTopicTitle] = useState<string | null>(null);
  const [testPage, setTestPage] = useState(1);
  const [testLimit] = useState(20);
  const [testTotal, setTestTotal] = useState(0);
  const [resetConfirm, setResetConfirm] = useState<{
    open: boolean;
    userId?: string;
    testId?: string;
    loading?: boolean;
  }>({ open: false });
  const [refreshKey, setRefreshKey] = useState(0);

  const [selectedStudent, setSelectedStudent] = useState<string | null>(null);
  const [studentReportData, setStudentReportData] = useState<StudentReportData | null>(null);
  const [studentReportLoading, setStudentReportLoading] = useState(false);
  const [availableLessons, setAvailableLessons] = useState<LessonOption[]>([]);
  const [availableTopics, setAvailableTopics] = useState<TopicOption[]>([]);
  const [selectedLessonFilter, setSelectedLessonFilter] = useState<string | null>(null);
  const [selectedTopicFilter, setSelectedTopicFilter] = useState<string | null>(null);
  const [selectedReportType, setSelectedReportType] = useState<StudentReportType>("all");

  const getStatusBadge = (status: string, deadline?: string, metadata?: any) => {
    if (status === "COMPLETED" && metadata?.completedLate) {
      return (
        <span className="inline-flex items-center px-2 sm:px-4 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium bg-amber-100 text-amber-800">
          <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
          Geç Tamamlandı
        </span>
      );
    };

    switch (status) {
      case "COMPLETED":
        return (
          <span className="inline-flex items-center px-2 sm:px-4 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            Yapıldı
          </span>
        );
      case "FAILED":
        return (
          <span className="inline-flex items-center px-2 sm:px-4 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            Yapılmadı
          </span>
        );
      case "PENDING":
        return (
          <span className="inline-flex items-center px-2 sm:px-4 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            Bekleyen
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 sm:px-4 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium bg-gray-100 text-gray-800">
            <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            Bilinmiyor
          </span>
        );
    }
  };

  const getTestStatusBadge = (status: string, score?: number) => {
    switch (status) {
      case "COMPLETED":
        return (
          <span className="inline-flex items-center px-2 sm:px-4 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            Yapıldı – Geçti
          </span>
        );
      case "FAILED":
        return (
          <span className="inline-flex items-center px-2 sm:px-4 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium bg-orange-100 text-orange-800">
            <XCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            Yapıldı – Kaldı
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 sm:px-4 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            Yapılmadı
          </span>
        );
    }
  };

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case "VIDEO":
        return <Video className="w-4 h-4 text-blue-600" />;
      case "AUDIO":
        return <Volume2 className="w-4 h-4 text-green-600" />;
      case "TEXT":
        return <FileText className="w-4 h-4 text-gray-600" />;
      case "FILE":
      case "PDF":
        return <File className="w-4 h-4 text-red-600" />;
      case "CODE":
        return <Code className="w-4 h-4 text-indigo-600" />;
      case "TEST":
      case "EXAM":
        return <ClipboardCheck className="w-4 h-4 text-orange-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getContentTypeText = (type: string) => {
    switch (type) {
      case "VIDEO":
        return "Video";
      case "AUDIO":
        return "Ses";
      case "TEXT":
        return "Metin";
      case "FILE":
        return "Dosya";
      case "CODE":
        return "Kod";
      case "TEST":
      case "EXAM":
        return "Test";
      default:
        return "İçerik";
    }
  };

  const getPerformanceLevel = (score: number) => {
    if (score >= 90)
      return {
        text: "Mükemmel",
        shortText: "Mük.",
        icon: Trophy,
        color: "text-yellow-600",
      };
    if (score >= 80)
      return {
        text: "Çok İyi",
        shortText: "Çok İyi",
        icon: Award,
        color: "text-green-600",
      };
    if (score >= 70)
      return {
        text: "İyi",
        shortText: "İyi",
        icon: TrendingUp,
        color: "text-blue-600",
      };
    if (score >= 50)
      return {
        text: "Orta",
        shortText: "Orta",
        icon: Target,
        color: "text-orange-600",
      };
    return {
      text: "Gelişti.",
      shortText: "Gelişti.",
      icon: AlertCircle,
      color: "text-red-600",
    };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getISOWeekYear = (dateString: string): number => {
    const date = new Date(dateString);
    const calendarYear = date.getFullYear();

    const getISOWeekStart = (year: number) => {
      const simple = new Date(year, 0, 4);
      const dayOfWeek = simple.getDay();
      const ISOWeekStart = new Date(simple);
      ISOWeekStart.setDate(simple.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

      return ISOWeekStart;
    };

    const isoWeekStart = getISOWeekStart(calendarYear);

    if (date < isoWeekStart) return calendarYear - 1;

    const nextYearISOStart = getISOWeekStart(calendarYear + 1);
    if (date >= nextYearISOStart) return calendarYear + 1;

    return calendarYear;
  };

  const getHomeworkYear = (homework: HomeworkReport["homework"] | null): number | null => {
    if (!homework) return null;

    if (homework.metadata?.year && typeof homework.metadata.year === "number") {
      return homework.metadata.year;
    }

    if (homework.deadline) {
      return getISOWeekYear(homework.deadline);
    }

    if (homework.createdAt) {
      return getISOWeekYear(homework.createdAt);
    }

    return null;
  };

  const clearFilters = () => {
    setSelectedStatus("all");
    setSelectedContentType("all");
    setSelectedDepartment(null);
    setStartDate(null);
    setEndDate(null);
    setPage(1);
  };

  const loadTestReports = async () => {
    try {
      setTestReportsLoading(true);
      if (
        !testSelectedClass ||
        !testSelectedLesson ||
        !(testSelectedSubClass && testSelectedSubClass.length > 0) ||
        !testSelectedTopic ||
        !testSelectedTest ||
        testSelectedTest.length === 0
      ) {
        setTestReports([]);
        setTestReportsLoading(false);
        return;
      }

      const departmentIdsToSend: string[] = (testSelectedSubClass || []).map(String);

      const payload = {
        lessonId: String(testSelectedLesson),
        departmentIds: departmentIdsToSend,
        topicId: String(testSelectedTopic),
        testIds: testSelectedTest.map(String),
        page: testPage,
        limit: testLimit,
      } as any;

      if (testSelectedStatus && testSelectedStatus !== "all") {
        payload.status = testSelectedStatus === "COMPLETED" ? 1 : testSelectedStatus === "PENDING" ? 2 : 3;
      }

      const [res, lessonDetail, sectionsList]: any = await Promise.all([
        ApiService.getTestReports(payload).catch(() => ({ data: [] })),
        ApiService.getLesson(String(testSelectedLesson)).catch(() => null),
        ApiService.getLessonSections(String(testSelectedLesson)).catch(() => null),
      ]);

      const lessonTitle = (lessonDetail?.data?.title || lessonDetail?.data?.name || lessonDetail?.title || lessonDetail?.name) ?? null;
      const topicFromList = (() => {
        const arr = (sectionsList?.data || sectionsList || []) as any[];
        const found = (Array.isArray(arr) ? arr : []).find(
          (s: any) => String(s.id || s._id || s.sectionId || "") === String(testSelectedTopic)
        );
        return found ? found.title || found.name || found.sectionTitle || null : null;
      })();
      const topicTitle = topicFromList || null;
      const parentDeptName = null;
      const childDeptName = null;

      setTestLessonTitle(lessonTitle);
      setTestTopicTitle(topicTitle);
      setTestParentDepartmentName(parentDeptName);
      setTestSubClassName(childDeptName);
      const list = (res?.data || res || []).map((row: any) => {
        return {
          id: `${row.user?.id || "u"}-${row.testId || "t"}`,
          status:
            String(row.status || "").toUpperCase() === "COMPLETED"
              ? "COMPLETED"
              : String(row.status || "").toUpperCase() === "FAILED"
                ? "FAILED"
                : "PENDING",
          score: row.stats && typeof row.stats.percent === "number" ? row.stats.percent : undefined,
          totalQuestions: (row.stats?.correct || 0) + (row.stats?.wrong || 0) + (row.stats?.pending || 0),
          correctAnswers: row.stats?.correct || 0,
          wrongAnswers: row.stats?.wrong || 0,
          blankAnswers: row.stats?.pending || 0,
          startedAt: row.startDate || row.createdAt || row.beginDate || undefined,
          completedAt: row.finishDate || new Date().toISOString(),
          user: {
            id: row.user?.id || "",
            firstName: row.user?.firstName || "",
            lastName: row.user?.lastName || "",
            email: row.user?.email || "",
            metadata: row.user?.metadata,
            department: row.user?.department || undefined,
          },
          test: {
            id: row.testId,
            title: row.title || "",
            class: parentDeptName || "",
            subClass: childDeptName || "",
            lesson: lessonTitle || "",
            topic: topicTitle || "",
          },
        } as TestReport;
      });

      setTestReports(list);
      const meta = (res && (res as any)?.meta) || null;
      if (meta) {
        setTestTotal(meta.totalItems || 0);
      } else {
        setTestTotal(Array.isArray((res as any)?.data) ? (res as any)?.data.length : list.length);
      }
    } catch (e) {
      setTestReports([]);
      setTestTotal(0);
    } finally {
      setTestReportsLoading(false);
    }
  };

  const handleTestFilter = () => {
    setTestFiltered(true);
    setTestPage(1);
  };

  const clearTestFilters = () => {
    setTestSelectedClass(null);
    setTestSelectedSubClass([]);
    setTestSelectedLesson(null);
    setTestSelectedTopic(null);
    setTestSelectedTest([]);
    setTestSelectedStatus("all");
    setTestStartDate(null);
    setTestEndDate(null);
    setTestReports([]);
    setTestFiltered(false);
    setTestPage(1);
    setTestTotal(0);
  };

  const loadReports = async () => {
    setLoadingList(true);
    try {
      const typeParam = selectedContentType === "all" ? null : selectedContentType === "EXAM" ? "EXAM" : selectedContentType;
      const statusParam = selectedStatus === "all" ? null : selectedStatus;

      const res = await ApiService.getHomeworkReports(page, limit, "createdAt", "DESC", {
        status: statusParam,
        type: typeParam,
        departmentId: selectedDepartment,
        startDate: startDate,
        endDate: endDate,
      });

      const payload = (res && (res as any)) || {};
      const meta = payload.meta || {
        page: 1,
        limit,
        totalItems: 0,
        totalPages: 1,
      };
      const data: HomeworkReport[] = payload.data || [];
      setRows(data);
      setTotal(meta.totalItems || 0);
      setPage(meta.page || 1);
      const deptMap = new Map<string, Department>();
      (data || []).forEach((r) => {
        const d = r?.user?.department;
        if (d && d.id) deptMap.set(d.id, { id: d.id, name: d.name, color: d.color });
      });
      setDepartments(Array.from(deptMap.values()).sort((a, b) => a.name.localeCompare(b.name, "tr")));
    } catch (e) {
      setRows([]);
      setTotal(0);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, [page, selectedStatus, selectedContentType, selectedDepartment, startDate, endDate]);

  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        const isMobile = window.matchMedia && window.matchMedia("(max-width: 640px)").matches;
        if (isMobile) setFilterOpen(false);
      }
    } catch (_) { }
  }, []);

  const loadStudentReport = async () => {
    if (!selectedStudent) {
      setStudentReportData(null);
      return;
    }

    try {
      setStudentReportLoading(true);
      const filters = {
        lessonId: selectedLessonFilter || undefined,
        topicId: selectedTopicFilter || undefined,
        type: selectedReportType,
      };

      const reportData = await ApiService.getStudentReport(selectedStudent, filters);
      setStudentReportData(reportData);
    } catch (error: any) {
      console.error("Error loading student report:", error);
      setStudentReportData(null);
      if (error.message?.includes("403") || error.message?.includes("Forbidden")) {
        alert("Bu öğrencinin raporlarına erişim yetkiniz yok.");
      } else if (error.message?.includes("404") || error.message?.includes("Not Found")) {
        alert("Öğrenci bulunamadı.");
      } else {
        alert("Öğrenci raporu yüklenirken bir hata oluştu. Lütfen tekrar deneyin.");
      }
    } finally {
      setStudentReportLoading(false);
    }
  };

  const loadAvailableLessons = async () => {
    if (!selectedStudent) {
      setAvailableLessons([]);
      return;
    }

    try {
      const lessons = await ApiService.getStudentAvailableLessons(selectedStudent);
      setAvailableLessons(lessons || []);
    } catch (error) {
      console.error("Error loading available lessons:", error);
      setAvailableLessons([]);
    }
  };

  const loadAvailableTopics = async () => {
    if (!selectedStudent || !selectedLessonFilter) {
      setAvailableTopics([]);
      return;
    }

    try {
      const topics = await ApiService.getStudentAvailableTopics(selectedStudent, selectedLessonFilter);
      setAvailableTopics(topics || []);
    } catch (error) {
      console.error("Error loading available topics:", error);
      setAvailableTopics([]);
    }
  };

  useEffect(() => {
    if (activeTab === "student" && selectedStudent) {
      loadAvailableLessons();
    }
  }, [selectedStudent, activeTab]);

  useEffect(() => {
    if (activeTab === "student" && selectedStudent && selectedLessonFilter) {
      loadAvailableTopics();
    }
  }, [selectedStudent, selectedLessonFilter, activeTab]);

  const handleStudentChange = (studentId: string | null) => {
    setSelectedStudent(studentId);
    setSelectedLessonFilter(null);
    setSelectedTopicFilter(null);
    setSelectedReportType("all");
    setStudentReportData(null);
  };

  const handleRefreshStudentReport = () => { };

  const renderTestReportItem = (report: TestReport) => {
    const performance = getPerformanceLevel(report.score ?? 0);
    const PerformanceIcon = performance.icon;

    return (
      <div
        key={report.id}
        className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 p-3 sm:p-4 mb-3">
        <div className="space-y-3 sm:space-y-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <ClipboardCheck className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 flex-shrink-0" />
            <h3 className="text-sm sm:text-lg font-semibold text-gray-900 truncate">{report.test.title || "(İçerik Bulunamadı)"}</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-2">Öğrenci Bilgileri</p>
              <div className="bg-gray-50 rounded-lg p-2 sm:p-3 space-y-2">
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <div className="flex items-center min-w-0 flex-1">
                    <User className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mr-2 flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                      {report.user.firstName} {report.user.lastName}
                    </span>
                  </div>
                  {report.user.department && (
                    <div className="flex-shrink-0">
                      <span
                        className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium text-white"
                        style={{
                          backgroundColor: report.user.department.color,
                        }}
                        title={report.user.department.name}>
                        <span className="max-w-[80px] sm:max-w-[120px] truncate">{report.user.department.name}</span>
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  {report.user?.email && (
                    <p className="text-xs text-gray-600 break-words">
                      <span className="font-medium">E-posta:</span>{" "}
                      <a
                        href={`mailto:${report.user.email}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline transition-colors">
                        {report.user.email}
                      </a>
                    </p>
                  )}
                  {report.user?.metadata?.schoolNumber && (
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">Okul No:</span> {report.user.metadata.schoolNumber || "(Okul No Bulunamadı)"}
                    </p>
                  )}
                  {report.user?.metadata?.tcNumber && (
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">TC No:</span> {report.user.metadata.tcNumber || "(TC No Bulunamadı)"}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-2">Ders Bilgileri</p>
              <div className="bg-gray-50 rounded-lg p-2 sm:p-3 space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center min-w-0 flex-1">
                    <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 mr-2 flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                      {report.test.lesson || "(Ders Bulunamadı)"}
                    </span>
                  </div>
                  <div className="flex-shrink-0 self-start sm:self-auto">
                    {testSelectedLesson && report.test?.id ? (
                      <a
                        href={`/lessons/play/${testSelectedLesson}?contentId=${report.test.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-white rounded-md transition-colors"
                        title="İçeriğe Git">
                        <ExternalLink className="w-3 h-3" />
                        <span className="hidden sm:inline">Git</span>
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-400 cursor-not-allowed">
                        <ExternalLink className="w-3 h-3" />
                        <span className="hidden sm:inline">Git</span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Bölüm:</span>{" "}
                    <span className="break-words">{report.test.topic || "(Bölüm Bulunamadı)"}</span>
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">İçerik:</span>{" "}
                    <span className="break-words">{report.test.title || "(İçerik Bulunamadı)"}</span>
                  </p>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-2">Tarih Bilgileri</p>
              <div className="bg-gray-50 rounded-lg p-2 sm:p-3 space-y-2">
                {report.status === "COMPLETED" || report.status === "FAILED" ? (
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-1">Tamamlanma Tarihi:</p>
                    <div className="flex items-start">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-xs sm:text-sm text-gray-900 break-words">{formatDate(report.completedAt)}</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-1">Tamamlanma Tarihi:</p>
                    <div className="flex items-start">
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-xs sm:text-sm text-gray-900 break-words">Henüz Tamamlanmadı</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-2">Puan Bilgileri</p>
              <div className="bg-gray-50 rounded-lg p-2 sm:p-3 space-y-2">
                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-3 items-center gap-1">
                    <span className="text-xs sm:text-sm font-medium text-gray-600">Puan:</span>
                    {report.score === undefined || report.score === null ? (
                      <span className="text-sm sm:text-base font-bold text-gray-500 text-center col-span-2">Puansız</span>
                    ) : (
                      <>
                        <div className="flex justify-center">
                          <div className="inline-flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 rounded-full bg-white/80 backdrop-blur-sm border border-gray-200 shadow-sm">
                            <PerformanceIcon className={`w-2.5 h-2.5 sm:w-3 sm:h-3 ${performance.color}`} />
                            <span className={`text-[10px] sm:text-xs font-bold ${performance.color}`}>{performance.shortText}</span>
                          </div>
                        </div>
                        <span className={`text-sm sm:text-base font-bold ${performance.color} text-right`}>
                          {Math.round(report.score)}%
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-600 text-center pt-1">
                    <span className="font-medium">Toplam:</span> {report.totalQuestions}
                  </p>
                  <div className="grid grid-cols-3 gap-1 text-[10px] sm:text-xs">
                    <div className="text-green-600 text-center">
                      <div className="font-medium">Doğru</div>
                      <div className="font-semibold">{report.correctAnswers}</div>
                    </div>
                    <div className="text-red-600 text-center">
                      <div className="font-medium">Yanlış</div>
                      <div className="font-semibold">{report.wrongAnswers}</div>
                    </div>
                    <div className="text-gray-500 text-center">
                      <div className="font-medium">Boş</div>
                      <div className="font-semibold">{report.blankAnswers}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 pt-2 border-t border-gray-100">
            <div className="flex justify-center sm:justify-start">{getTestStatusBadge(report.status, report.score)}</div>
            <div className="w-full sm:w-auto flex items-center gap-2 flex-wrap sm:flex-nowrap justify-center sm:justify-end">
              {checkPermission("test_report_delete") && (report.status === "COMPLETED" || report.status === "FAILED") && (
                <button
                  onClick={() => {
                    setResetConfirm({
                      open: true,
                      userId: report.user.id,
                      testId: report.test.id,
                      loading: false,
                    });
                  }}
                  className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs sm:text-sm inline-flex items-center justify-center gap-1.5 sm:gap-2 min-w-0"
                  title="Bu kullanıcı için testi sıfırla">
                  <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="truncate">Testi Sıfırla</span>
                </button>
              )}
              {report.status === "PENDING" ? null : (
                <button
                  onClick={async () => {
                    try {
                      setLoadingTestReport(true);
                      const detail = await ApiService.getTestReportDetail(String(report.test.id), String(report.user.id));
                      setViewingTestReport(detail);
                    } catch (error) {
                      alert("Test raporu yüklenirken bir hata oluştu.");
                    } finally {
                      setLoadingTestReport(false);
                    }
                  }}
                  disabled={loadingTestReport}
                  className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm inline-flex items-center justify-center gap-1.5 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-0">
                  <Eye className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="truncate">{loadingTestReport ? "Yükleniyor..." : "Raporu Görüntüle"}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderReportItem = (report: HomeworkReport) => {
    const lessonSectionContent = report.homework?.lessonSectionContent || null;
    const lessonSection = report.homework?.lessonSection || null;
    const contentTitleSafe = lessonSectionContent?.title || "(İçerik Bulunamadı)";
    const sectionTitleSafe = lessonSection?.title || "(Bölüm Bulunamadı)";
    const contentIdSafe = lessonSectionContent?.id || null;
    const homeworkYear = getHomeworkYear(report.homework);
    const homeworkTitle = report.homework?.title || "(Ödev Başlığı Bulunamadı)";
    const displayTitle = homeworkYear ? `${homeworkTitle} (${homeworkYear})` : homeworkTitle;

    return (
      <div
        key={report.id}
        className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200 p-3 sm:p-4 mb-3">
        <div className="space-y-4">
          <div className="flex items-center space-x-2 sm:space-x-3">
            {getContentTypeIcon(lessonSectionContent?.type || "")}
            <h3 className="text-sm sm:text-lg font-semibold text-gray-900 truncate">
              {displayTitle}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-2">Öğrenci Bilgileri</p>
              <div className="bg-gray-50 rounded-lg p-2 sm:p-3 space-y-2">
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <div className="flex items-center min-w-0 flex-1">
                    <User className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mr-2 flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                      {report.user?.firstName || "(Bilinmiyor)"} {report.user?.lastName || ""}
                    </span>
                  </div>
                  {report.user?.department && (
                    <div className="flex-shrink-0">
                      <span
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
                        style={{
                          backgroundColor: report.user.department.color,
                        }}
                        title={report.user.department.name}>
                        <span className="max-w-[120px] truncate">{report.user.department.name}</span>
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  {report.user?.email && (
                    <p className="text-xs text-gray-600 break-words">
                      <span className="font-medium">E-posta:</span>{" "}
                      <a
                        href={`mailto:${report.user.email}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline transition-colors">
                        {report.user.email}
                      </a>
                    </p>
                  )}
                  {report.user?.metadata?.schoolNumber && (
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">Okul No:</span> {report.user.metadata.schoolNumber}
                    </p>
                  )}
                  {report.user?.metadata?.tcNumber && (
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">TC No:</span> {report.user.metadata.tcNumber}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-2">Ders Bilgileri</p>
              <div className="bg-gray-50 rounded-lg p-2 sm:p-3 space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center min-w-0 flex-1">
                    <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 mr-2 flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                      {report.homework?.lesson?.title || "(Ders Bulunamadı)"}
                    </span>
                  </div>
                  <div className="flex-shrink-0">
                    {contentIdSafe ? (
                      <a
                        href={`/lessons/play/${report.homework?.lesson?.id || ""}?contentId=${contentIdSafe}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-white rounded-md transition-colors"
                        title="İçeriğe Git">
                        <ExternalLink className="w-3 h-3" />
                        <span className="hidden sm:inline">Git</span>
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-400 cursor-not-allowed">
                        <ExternalLink className="w-3 h-3" />
                        <span className="hidden sm:inline">Git</span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Bölüm:</span> <span className="break-words">{sectionTitleSafe}</span>
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">İçerik:</span> <span className="break-words">{contentTitleSafe}</span>
                  </p>
                  <div className="flex items-center">
                    <span className="text-xs font-medium text-gray-600 mr-2 flex-shrink-0">Tür:</span>
                    {getContentTypeIcon(lessonSectionContent?.type || "")}
                    <span className="text-xs text-gray-600 ml-1">{getContentTypeText(lessonSectionContent?.type || "")}</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-2">Tarih Bilgileri</p>
              <div className="bg-gray-50 rounded-lg p-2 sm:p-3 space-y-2">
                <div className="space-y-2">
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-1">
                      {report.homework?.status === "COMPLETED" ? "Tamamlanma" : "Oluşturulma"} Tarihi:
                    </p>
                    <div className="flex items-start">
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-900 break-words">
                        {formatDate(
                          report.homework?.status === "COMPLETED" && report.homework?.finishedAt
                            ? report.homework.finishedAt
                            : report.homework?.createdAt || new Date().toISOString()
                        )}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-1">Son Tarih:</p>
                    <div className="flex items-start">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-900 break-words">
                        {formatDate(report.homework?.deadline || new Date().toISOString())}
                      </span>
                    </div>
                    {report.homework?.deadline && new Date(report.homework.deadline) < new Date() && (
                      <div className="text-xs text-red-600 font-medium mt-1">SÜRE GEÇTİ</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-500 mb-2">Puan Bilgileri</p>
              <div className="bg-gray-50 rounded-lg p-2 sm:p-3 space-y-2">
                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-3 items-center">
                    <span className="text-xs sm:text-sm font-medium text-gray-600">Puan:</span>
                    {report.homework?.status === "FAILED" || report.reportData?.summary?.score_percent === undefined ? (
                      <span className="text-base sm:text-lg font-bold text-gray-500 text-center col-span-2">Puansız</span>
                    ) : (
                      (() => {
                        const performance = getPerformanceLevel(report.reportData.summary.score_percent);
                        const PerformanceIcon = performance.icon;
                        return (
                          <>
                            <div className="flex justify-center">
                              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/80 backdrop-blur-sm border border-gray-200 shadow-sm">
                                <PerformanceIcon className={`w-3 h-3 ${performance.color}`} />
                                <span className={`text-xs font-bold ${performance.color}`}>{performance.shortText}</span>
                              </div>
                            </div>
                            <span className={`text-base sm:text-lg font-bold ${performance.color} text-right`}>
                              {Math.round(report.reportData.summary.score_percent)}%
                            </span>
                          </>
                        );
                      })()
                    )}
                  </div>
                </div>
                {report.reportData?.summary && (
                  <div className="space-y-1">
                    {report.reportData.summary.total_questions && (
                      <p className="text-xs text-gray-600 text-center pt-1">
                        <span className="font-medium">Toplam Soru:</span> {report.reportData.summary.total_questions}
                      </p>
                    )}
                    {report.reportData.summary.total_questions && (
                      <div className="grid grid-cols-3 gap-1 text-xs">
                        <div className="text-green-600 text-center">
                          <div className="font-medium">Doğru:</div>
                          <div>{report.reportData.summary.correct || 0}</div>
                        </div>
                        <div className="text-red-600 text-center">
                          <div className="font-medium">Yanlış:</div>
                          <div>{report.reportData.summary.wrong || 0}</div>
                        </div>
                        <div className="text-gray-500 text-center">
                          <div className="font-medium">Boş:</div>
                          <div>{report.reportData.summary.unanswered || 0}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <div className="flex justify-center sm:justify-start">
              {getStatusBadge(report.homework?.status || "UNKNOWN", report.homework?.deadline || new Date().toISOString(), report.homework?.metadata)}
            </div>
            <div className="w-full sm:w-auto">
              {report.reportData && (
                <button
                  onClick={() => setViewingReport(report)}
                  className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm flex items-center justify-center gap-2">
                  <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                  Raporu Görüntüle
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const isIOSDevice = () => {
    if ("userAgentData" in navigator && (navigator as any).userAgentData) {
      const uaData = (navigator as any).userAgentData;
      return uaData.platform === "macOS" && navigator.maxTouchPoints > 1;
    }

    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isMacWithTouch = /Mac/.test(userAgent) && navigator.maxTouchPoints > 1;

    return isIOS || isMacWithTouch;
  };

  const exportReports = async () => {
    try {
      const filters: any = {};
      if (selectedStatus !== "all") {
        filters.status = selectedStatus;
      }
      if (selectedContentType !== "all") {
        filters.type = selectedContentType === "EXAM" ? "EXAM" : selectedContentType;
      }
      if (selectedDepartment) {
        filters.departmentId = selectedDepartment;
      }
      if (startDate) {
        filters.startDate = startDate;
      }
      if (endDate) {
        filters.endDate = endDate;
      }

      const blob = await ApiService.exportHomeworkReports(filters);

      const now = new Date();
      const dateStr = now.toISOString().split("T")[0];
      const filename = `odev_raporlari_${dateStr}.xlsx`;

      if (isIOSDevice()) {
        const url = URL.createObjectURL(blob);
        const newWindow = window.open();

        if (newWindow) {
          newWindow.location.href = url;

          setTimeout(() => {
            alert("Dosya yeni sekmede açıldı. Lütfen dosyayı kaydetmek için tarayıcınızın paylaş menüsünü kullanın.");

            URL.revokeObjectURL(url);
          }, 500);
        } else {
          window.location.href = url;
        }
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Excel dışa aktarma hatası:", error);
      alert("Dışa aktarma sırasında bir hata oluştu. Lütfen tekrar deneyin.");
    }
  };

  const renderActions = () => (
    <div className="flex flex-row flex-wrap gap-2">
      <button
        onClick={() => setFilterOpen((prev) => !prev)}
        className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 ${filterOpen
          ? "bg-blue-600 text-white hover:bg-blue-700 border-blue-600"
          : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          }`}
        title="Filtrele"
        aria-pressed={filterOpen}>
        <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        <span>Filtrele</span>
      </button>
      <button
        onClick={exportReports}
        className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium bg-orange-600 text-white hover:bg-orange-700 border border-orange-600 flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2">
        <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        <span>Dışa Aktar</span>
      </button>
    </div>
  );

  const renderFilterArea = () => (
    <ReportFilter
      selectedStatus={selectedStatus}
      onStatusChange={(v) => {
        setSelectedStatus(v);
        setPage(1);
      }}
      selectedContentType={selectedContentType}
      onContentTypeChange={(v) => {
        setSelectedContentType(v);
        setPage(1);
      }}
      selectedDepartment={selectedDepartment}
      onDepartmentChange={(v) => {
        setSelectedDepartment(v);
        setPage(1);
      }}
      departments={departments}
      startDate={startDate}
      endDate={endDate}
      onDateChange={(s, e) => {
        setStartDate(s);
        setEndDate(e);
        setPage(1);
      }}
      onClearFilters={clearFilters}
    />
  );

  if (isParentOnly) {
    return (
      <div className="h-full bg-gray-50 flex flex-col">
        <div className="flex-1 min-h-0 px-3 sm:px-4">
          <div className="h-full bg-gray-50 relative rounded-lg shadow-sm flex flex-col mr-1">
            <div className="px-6 py-6 w-full flex-1 flex flex-col min-h-0">
              <div className="flex-1 min-h-0 overflow-y-auto">
                <ParentReportsTab />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50 flex flex-col">
      <div className="flex-1 min-h-0 px-3 sm:px-4">
        <div className="h-full bg-gray-50 relative rounded-lg shadow-sm flex flex-col mr-1">
          <div className="px-6 py-6 w-full flex-1 flex flex-col min-h-0">
            <div className="mb-4 sm:mb-6 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">Raporlar</span>
                </div>
                <div className="flex items-center gap-4 sm:gap-6 text-sm text-gray-600 overflow-x-auto scrollbar-hide w-full sm:w-auto -mx-2 px-2 whitespace-nowrap">
                  {hasHomeworkReportList && (
                    <button
                      onClick={() => {
                        setActiveTab("homework");
                        if (activeTab === "test") {
                          clearTestFilters();
                        }
                      }}
                      className={`pb-3 transition-all duration-200 flex-shrink-0 ${activeTab === "homework" ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"
                        }`}>
                      Ödev Raporları
                    </button>
                  )}
                  {hasTestReportList && (
                    <button
                      onClick={() => {
                        setActiveTab("test");
                        clearTestFilters();
                      }}
                      className={`pb-3 transition-all duration-200 flex-shrink-0 ${activeTab === "test" ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"
                        }`}>
                      Test Raporları
                    </button>
                  )}
                  {hasStudentReportList && !isParentOnly && (
                    <button
                      onClick={() => {
                        setActiveTab("student");

                        clearTestFilters();
                      }}
                      className={`pb-3 transition-all duration-200 flex-shrink-0 ${activeTab === "student" ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"
                        }`}>
                      Öğrenci Raporları
                    </button>
                  )}
                  {hasDepartmentReportList && (
                    <button
                      onClick={() => {
                        setActiveTab("department");
                        clearTestFilters();
                      }}
                      className={`pb-3 transition-all duration-200 flex-shrink-0 ${activeTab === "department" ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"
                        }`}>
                      Şube Raporları
                    </button>
                  )}
                  {hasTeacherReportList && !isParentOnly && (
                    <button
                      onClick={() => {
                        setActiveTab("teacher");
                        clearTestFilters();
                      }}
                      className={`pb-3 transition-all duration-200 flex-shrink-0 ${activeTab === "teacher" ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"
                        }`}>
                      Öğretmen Raporları
                    </button>
                  )}
                  {hasErrorBookletReportList && !isParentOnly && (
                    <button
                      onClick={() => {
                        setActiveTab("error-booklet");
                        clearTestFilters();
                      }}
                      className={`pb-3 transition-all duration-200 flex-shrink-0 ${activeTab === "error-booklet" ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"
                        }`}>
                      Hata Kitapçığı Raporları
                    </button>
                  )}
                  {hasParentReportList && (
                    <button
                      onClick={() => {
                        setActiveTab("parent");
                        clearTestFilters();
                      }}
                      className={`pb-3 transition-all duration-200 flex-shrink-0 ${activeTab === "parent" ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"
                        }`}>
                      Veli Raporları
                    </button>
                  )}
                  {hasSuccessScoreReportList && !isParentOnly && (
                    <button
                      onClick={() => {
                        setActiveTab("success-score");
                        clearTestFilters();
                      }}
                      className={`pb-3 transition-all duration-200 flex-shrink-0 ${activeTab === "success-score" ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"
                        }`}>
                      Başarı Puan Raporu
                    </button>
                  )}
                  {hasSuccessScoreReportList && !isParentOnly && (
                    <button
                      onClick={() => {
                        setActiveTab("monthly-success-score");
                        clearTestFilters();
                      }}
                      className={`pb-3 transition-all duration-200 flex-shrink-0 ${activeTab === "monthly-success-score" ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"
                        }`}>
                      Aylık Başarı Puan Raporu
                    </button>
                  )}
                  {hasSuccessScoreReportList && !isParentOnly && (
                    <button
                      onClick={() => {
                        setActiveTab('branch-success-score');
                        clearTestFilters();
                      }}
                      className={`pb-3 transition-all duration-200 flex-shrink-0 ${activeTab === "branch-success-score" ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"
                        }`}>
                      Şube Başarı Puan Raporu
                    </button>
                  )}
                  {hasAIObservation && (
                    <button
                      onClick={() => {
                        setActiveTab("ai-observation");
                        clearTestFilters();
                      }}
                      className={`pb-3 transition-all duration-200 flex-shrink-0 ${activeTab === "ai-observation" ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"
                        }`}>
                      AI Gözlem
                    </button>
                  )}
                </div>
              </div>
            </div>

            {activeTab === "test" && hasTestReportList && !testFiltered && (
              <div className="mb-6">
                <TestHorizontalFilter
                  selectedClass={testSelectedClass}
                  onClassChange={setTestSelectedClass}
                  selectedSubClass={testSelectedSubClass}
                  onSubClassChange={setTestSelectedSubClass}
                  selectedLesson={testSelectedLesson}
                  onLessonChange={setTestSelectedLesson}
                  selectedTopic={testSelectedTopic}
                  onTopicChange={setTestSelectedTopic}
                  selectedTest={testSelectedTest}
                  onTestChange={setTestSelectedTest}
                  selectedStatus={testSelectedStatus}
                  onStatusChange={setTestSelectedStatus}
                  startDate={testStartDate}
                  endDate={testEndDate}
                  onDateChange={(start, end) => {
                    setTestStartDate(start);
                    setTestEndDate(end);
                  }}
                  onFilter={handleTestFilter}
                  onParentDepartmentChange={setTestParentDepartmentId}
                />
              </div>
            )}

            {activeTab === "test" && hasTestReportList && testFiltered && <></>}

            {activeTab === "homework" && hasHomeworkReportList && (
              <div className="grid grid-cols-1 md:grid-cols-3 items-center mb-6 gap-4">
                <div className="flex items-center space-x-4 justify-start">{!false && renderActions()}</div>
                <div />
                <div className="flex items-center space-x-4 justify-start md:justify-end">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span>{total > 0 ? `${(page - 1) * limit + 1}-${Math.min(page * limit, total)} / ${total}` : "0 / 0"}</span>
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="disabled:opacity-50 disabled:cursor-not-allowed">
                      <ChevronLeft className="h-4 w-4 text-gray-400" />
                    </button>
                    <button
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page * limit >= total}
                      className="disabled:opacity-50 disabled:cursor-not-allowed">
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-1 min-h-0 w-full gap-4">
              {activeTab === "homework" && hasHomeworkReportList && filterOpen && (
                <div className="flex-shrink-0 h-full overflow-y-auto mr-2 transition-all duration-300">{renderFilterArea()}</div>
              )}
              {activeTab === "test" && hasTestReportList && testFiltered && null}
              <div className="flex-1 min-h-0 flex flex-col">
                {activeTab === "homework" && hasHomeworkReportList && (
                  <>
                    {rows.length === 0 && !loadingList ? (
                      <div className="text-center py-12">
                        <p className="text-gray-500">Filtrelere uygun ödev raporu bulunamadı.</p>
                      </div>
                    ) : (
                      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
                        <div className="pb-8">{rows.map((r) => renderReportItem(r))}</div>
                      </div>
                    )}
                  </>
                )}

                {activeTab === "test" && hasTestReportList && !testFiltered && null}

                {activeTab === "student" && hasStudentReportList && (
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <StudentReportsTab
                      selectedStudent={selectedStudent}
                      onStudentChange={handleStudentChange}
                      availableLessons={availableLessons}
                      availableTopics={availableTopics}
                      selectedLessonFilter={selectedLessonFilter}
                      onLessonFilterChange={setSelectedLessonFilter}
                      selectedTopicFilter={selectedTopicFilter}
                      onTopicFilterChange={setSelectedTopicFilter}
                      selectedReportType={selectedReportType}
                      onReportTypeChange={setSelectedReportType}
                      onRefresh={handleRefreshStudentReport}
                    />
                  </div>
                )}

                {activeTab === "department" && hasDepartmentReportList && (
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <DepartmentHomeworkReportTab />
                  </div>
                )}

                {activeTab === "parent" && hasParentReportList && (
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <ParentReportsTab />
                  </div>
                )}

                {activeTab === "teacher" && hasTeacherReportList && (
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <TeacherHomeworkReportTab />
                  </div>
                )}

                {activeTab === "error-booklet" && hasErrorBookletReportList && (
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <ErrorBookletReportsTab />
                  </div>
                )}

                {activeTab === "success-score" && hasSuccessScoreReportList && (
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <SuccessScoreReportTab mode="instant" />
                  </div>
                )}

                {activeTab === "monthly-success-score" && hasSuccessScoreReportList && (
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <SuccessScoreReportTab mode="monthly" />
                  </div>
                )}

                {activeTab === 'branch-success-score' && hasSuccessScoreReportList && (
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <BranchSuccessScoreReportTab />
                  </div>
                )}

                {activeTab === "ai-observation" && hasAIObservation && (
                  <AIObservationTab />
                )}

                {activeTab === "test" && hasTestReportList && testFiltered && (
                  <DynamicList
                    key={refreshKey}
                    header={false}
                    title=""
                    isSearch={false}
                    isPagination={true}
                    actions={
                      <div className="flex flex-row flex-wrap gap-2">
                        <button
                          onClick={clearTestFilters}
                          className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-white text-gray-700 hover:bg-gray-50 border-gray-300">
                          <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          <span>Filtrele</span>
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              if (
                                !testSelectedLesson ||
                                !(testSelectedSubClass && testSelectedSubClass.length > 0) ||
                                !testSelectedTopic ||
                                !testSelectedTest ||
                                testSelectedTest.length === 0
                              ) {
                                return;
                              }
                              const payload: any = {
                                lessonId: String(testSelectedLesson),
                                departmentIds: (testSelectedSubClass || []).map(String),
                                topicId: String(testSelectedTopic),
                                testIds: (testSelectedTest || []).map(String),
                              };
                              if (testSelectedStatus && testSelectedStatus !== "all") {
                                payload.status = testSelectedStatus === "COMPLETED" ? 1 : testSelectedStatus === "PENDING" ? 2 : 3;
                              }
                              const blob = await ApiService.exportTestReports(payload);
                              const now = new Date();
                              const dateStr = now.toISOString().split("T")[0];
                              const filename = `test_raporlari_${dateStr}.xlsx`;
                              const url = URL.createObjectURL(blob);
                              const link = document.createElement("a");
                              link.href = url;
                              link.download = filename;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              URL.revokeObjectURL(url);
                            } catch (e) {
                              alert("Dışa aktarma sırasında bir hata oluştu. Lütfen tekrar deneyin.");
                            }
                          }}
                          className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium bg-orange-600 text-white hover:bg-orange-700 border border-orange-600 flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2">
                          <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          <span>Dışa Aktar</span>
                        </button>
                      </div>
                    }
                    path={`test-reports:${String(testSelectedLesson || "-")}:${(testSelectedSubClass || []).join(",")}:${String(
                      testSelectedTopic || "-"
                    )}:${(testSelectedTest || []).join(",")}:${String(testSelectedStatus)}`}
                    fetcher={async (page) => {
                      if (
                        !testSelectedLesson ||
                        !(testSelectedSubClass && testSelectedSubClass.length > 0) ||
                        !testSelectedTopic ||
                        !testSelectedTest ||
                        testSelectedTest.length === 0
                      ) {
                        return {
                          data: [],
                          meta: {
                            page,
                            totalPages: 1,
                            totalItems: 0,
                            limit: 20,
                          },
                        };
                      }
                      const departmentIdsToSend = (testSelectedSubClass || []).map(String);
                      const payload: any = {
                        lessonId: String(testSelectedLesson),
                        departmentIds: departmentIdsToSend,
                        topicId: String(testSelectedTopic),
                        testIds: testSelectedTest.map(String),
                        page,
                        limit: 20,
                      };
                      if (testSelectedStatus && testSelectedStatus !== "all") {
                        payload.status = testSelectedStatus === "COMPLETED" ? 1 : testSelectedStatus === "PENDING" ? 2 : 3;
                      }
                      const [res, lessonDetail, sectionsList]: any = await Promise.all([
                        ApiService.getTestReports(payload).catch(() => ({
                          data: [],
                        })),
                        ApiService.getLesson(String(testSelectedLesson)).catch(() => null),
                        ApiService.getLessonSections(String(testSelectedLesson)).catch(() => null),
                      ]);
                      const lessonTitle =
                        (lessonDetail?.data?.title || lessonDetail?.data?.name || lessonDetail?.title || lessonDetail?.name) ?? null;
                      const topicFromList = (() => {
                        const arr = (sectionsList?.data || sectionsList || []) as any[];
                        const found = (Array.isArray(arr) ? arr : []).find(
                          (s: any) => String(s.id || s._id || s.sectionId || "") === String(testSelectedTopic)
                        );
                        return found ? found.title || found.name || found.sectionTitle || null : null;
                      })();
                      const topicTitle = topicFromList || null;
                      const mapped: TestReport[] = (res?.data || res || []).map((row: any) => ({
                        id: `${row.user?.id || "u"}-${row.testId || "t"}`,
                        status:
                          String(row.status || "").toUpperCase() === "COMPLETED"
                            ? "COMPLETED"
                            : String(row.status || "").toUpperCase() === "FAILED"
                              ? "FAILED"
                              : "PENDING",
                        score: row.stats && typeof row.stats.percent === "number" ? row.stats.percent : undefined,
                        totalQuestions: (row.stats?.correct || 0) + (row.stats?.wrong || 0) + (row.stats?.pending || 0),
                        correctAnswers: row.stats?.correct || 0,
                        wrongAnswers: row.stats?.wrong || 0,
                        blankAnswers: row.stats?.pending || 0,
                        startedAt: row.startDate || row.createdAt || row.beginDate || undefined,
                        completedAt: row.finishDate || new Date().toISOString(),
                        user: {
                          id: row.user?.id || "",
                          firstName: row.user?.firstName || "",
                          lastName: row.user?.lastName || "",
                          email: row.user?.email || "",
                          metadata: row.user?.metadata,
                          department: row.user?.department || undefined,
                        },
                        test: {
                          id: row.testId,
                          title: row.title || "",
                          class: "",
                          subClass: "",
                          lesson: lessonTitle || "",
                          topic: topicTitle || "",
                        },
                      }));
                      const meta = (res && (res as any)?.meta) || {
                        page,
                        totalPages: 1,
                        totalItems: mapped.length,
                        limit: 20,
                      };
                      return { data: mapped, meta };
                    }}
                    item={(row) => renderTestReportItem(row as TestReport)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {viewingReport && viewingReport.reportData && (
        <Modal isOpen={true} onClose={() => setViewingReport(null)} title="Öğrenci Ödev Raporu" size="lg">
          <StudentReportView homeworkReport={viewingReport} onClose={() => setViewingReport(null)} />
        </Modal>
      )}

      {viewingTestReport && (
        <Modal isOpen={true} onClose={() => setViewingTestReport(null)} title="Öğrenci Test Raporu" size="lg">
          <UnifiedReportModalContent report={viewingTestReport} />
        </Modal>
      )}

      <ConfirmModal
        isOpen={Boolean(resetConfirm.open)}
        onClose={() => setResetConfirm({ open: false })}
        onConfirm={async () => {
          if (!resetConfirm.testId || !resetConfirm.userId) return;
          try {
            setResetConfirm((s) => ({ ...s, loading: true }));
            await ApiService.resetUserTest(String(resetConfirm.testId), String(resetConfirm.userId));
            setResetConfirm({ open: false });
            if (activeTab === "test") {
              if (testFiltered) {
                await loadTestReports();
                setRefreshKey((prev) => prev + 1);
              }
            } else {
              await loadReports();
            }
          } catch (e: any) {
            alert(e?.message || "Test sıfırlanırken bir hata oluştu.");
            setResetConfirm((s) => ({ ...s, loading: false }));
          }
        }}
        title="Testi Sıfırla"
        message="Bu kullanıcıya ait test verileri (sınav ve cevaplar) silinecektir. Devam etmek istediğinize emin misiniz?"
        confirmText="Evet, Sıfırla"
        cancelText="Vazgeç"
        confirmButtonVariant="danger"
        isLoading={Boolean(resetConfirm.loading)}
      />
    </div>
  );

  function UnifiedReportModalContent({ report }: { report: any }) {
    const normalized: any = useMemo(() => {
      if (report?.homework) return report;
      const user = report?.user || {};
      const lesson = report?.lesson || {};
      const section = report?.section || {};
      const test = report?.test || {};
      const stats = report?.stats || {};
      const summary = {
        score_percent: Math.round(Number(stats.percent ?? 0)),
        total_questions: Number(report?.totalQuestions ?? 0),
        correct: Number(stats.correct ?? 0),
        wrong: Number(stats.wrong ?? 0),
        unanswered: Number(stats.pending ?? 0),
      };
      const items = ((report?.questions as any[]) || []).map((q: any, idx: number) => {
        const isCorrect = q?.isCorrect;
        let result = "wrong";
        if (isCorrect === true) result = "correct";
        else if (isCorrect === null || q?.userAnswer === null || q?.userAnswer === undefined) result = "unanswered";
        return {
          id: String(q?.id || ""),
          qid: String(q?.id || ""),
          order: typeof q?.order === "number" ? q.order : idx + 1,
          options: q?.options,
          studentAnswer: q?.userAnswer === null || q?.userAnswer === undefined ? "" : String(q.userAnswer),
          correctAnswer: q?.correctOption === null || q?.correctOption === undefined ? "" : String(q.correctOption),
          result,
        } as any;
      });
      const reportData = { summary, items };
      const mapped: HomeworkReport = {
        id: String(report?.exam?.id || report?.test?.id || ""),
        reportData,
        reportHtml: "",
        createdAt: String(report?.exam?.createdAt || new Date().toISOString()),
        updatedAt: String(report?.exam?.endDate || new Date().toISOString()),
        homework: {
          id: String(test?.id || ""),
          title: String(test?.title || "(İçerik Bulunamadı)"),
          status: String(report?.status || "PENDING").toUpperCase(),
          createdAt: String(report?.exam?.startDate || new Date().toISOString()),
          deadline: String(report?.exam?.endDate || new Date().toISOString()),
          lesson: {
            id: String(lesson?.id || ""),
            title: String(lesson?.title || "(Ders Bulunamadı)"),
          },
          lessonSection: {
            id: String(section?.id || ""),
            title: String(section?.title || "(Bölüm Bulunamadı)"),
          },
          lessonSectionContent: {
            id: String(test?.id || ""),
            title: String(test?.title || "(İçerik Bulunamadı)"),
            type: String(test?.type || "TEST"),
          },
          finishedAt: report?.exam?.endDate || null,
        },
        user: {
          id: String(user?.id || ""),
          firstName: String(user?.firstName || ""),
          lastName: String(user?.lastName || ""),
          email: String(user?.email || ""),
          metadata: user?.metadata,
          department: user?.department
            ? {
              id: String(user.department.id),
              name: String(user.department.name),
              color: String(user.department.color),
            }
            : undefined,
        },
      } as any;
      return mapped;
    }, [report]);

    return <StudentReportView homeworkReport={normalized} onClose={() => { }} hideDeadline={true} />;
  }
}